import { supabase } from './supabase';
import {
  PhilippineHoliday,
  HolidayWorkRecord,
  HolidayPayCalculation,
  HolidayCheckResult,
  HolidayPaySummary,
  getHolidayPayRate
} from '../types/holiday';
import { calculateHourlyRate } from '../utils/philippinePayrollUtils';

export const holidayService = {
  // Get all holidays for a specific year
  async getHolidaysByYear(year: number): Promise<PhilippineHoliday[]> {
    try {
      const { data, error } = await supabase
        .from('philippine_holidays')
        .select('*')
        .eq('year', year)
        .eq('is_nationwide', true)
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching holidays:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getHolidaysByYear:', error);
      return [];
    }
  },

  // Get all holidays (year-independent)
  async getAllHolidays(): Promise<PhilippineHoliday[]> {
    try {
      const { data, error } = await supabase
        .from('philippine_holidays')
        .select('*')
        .eq('is_nationwide', true)
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching all holidays:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllHolidays:', error);
      return [];
    }
  },

  // Get holidays within a date range
  async getHolidaysByDateRange(startDate: string, endDate: string): Promise<PhilippineHoliday[]> {
    try {
      const { data, error } = await supabase
        .from('philippine_holidays')
        .select('*')
        .gte('date', startDate)
        .lte('date', endDate)
        .eq('is_nationwide', true)
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching holidays by date range:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getHolidaysByDateRange:', error);
      return [];
    }
  },

  // Check if a specific date is a holiday (year-specific)
  async isHoliday(date: string): Promise<HolidayCheckResult> {
    try {
      const { data, error } = await supabase
        .rpc('is_philippine_holiday', { check_date: date });

      if (error) {
        console.error('Error checking holiday:', error);
        return { is_holiday: false };
      }

      if (data && data.length > 0) {
        const result = data[0];
        return {
          is_holiday: result.is_holiday,
          holiday_name: result.holiday_name,
          holiday_type: result.holiday_type,
          holiday_id: result.holiday_id
        };
      }

      return { is_holiday: false };
    } catch (error) {
      console.error('Error in isHoliday:', error);
      return { is_holiday: false };
    }
  },

  // Check if a specific date is a holiday based on month and day only (year-independent)
  async isHolidayByMonthDay(date: string): Promise<HolidayCheckResult> {
    try {
      const checkDate = new Date(date);
      const month = checkDate.getMonth() + 1; // JavaScript months are 0-indexed
      const day = checkDate.getDate();
      
      console.log(`Checking for holidays on month ${month}, day ${day}`);
      
      // Get all holidays and filter by month/day in JavaScript
      const { data, error } = await supabase
        .from('philippine_holidays')
        .select('*')
        .eq('is_nationwide', true)
        .order('year', { ascending: false });

      if (error) {
        console.error('Error checking holiday by month/day:', error);
        return { is_holiday: false };
      }

      if (data && data.length > 0) {
        // Filter holidays by month and day
        const matchingHolidays = data.filter(holiday => {
          const holidayDate = new Date(holiday.date);
          const holidayMonth = holidayDate.getMonth() + 1;
          const holidayDay = holidayDate.getDate();
          return holidayMonth === month && holidayDay === day;
        });
        
        if (matchingHolidays.length > 0) {
          // Get the most recent holiday (highest year)
          const holiday = matchingHolidays[0];
          console.log(`Found holiday: ${holiday.name} (${holiday.type})`);
          return {
            is_holiday: true,
            holiday_name: holiday.name,
            holiday_type: holiday.type,
            holiday_id: holiday.id
          };
        }
      }

      console.log('No holiday found for this date');
      return { is_holiday: false };
    } catch (error) {
      console.error('Error in isHolidayByMonthDay:', error);
      return { is_holiday: false };
    }
  },

  // Calculate holiday pay for given parameters
  calculateHolidayPay(
    hourlyRate: number,
    hoursWorked: number,
    holidayType: PhilippineHoliday['type']
  ): HolidayPayCalculation {
    const rates = getHolidayPayRate(holidayType);
    const regularPay = hourlyRate * hoursWorked;
    
    let holidayPay: number;
    let additionalPay: number;
    
    if (holidayType === 'regular') {
      // Regular Holiday: Double pay + 60% additional
      holidayPay = hourlyRate * hoursWorked * rates.base_rate; // 200%
      additionalPay = hourlyRate * hoursWorked * rates.additional_rate; // 60%
    } else {
      // Special holidays: 130% pay
      holidayPay = hourlyRate * hoursWorked * rates.base_rate; // 130%
      additionalPay = 0;
    }
    
    const totalPay = holidayPay + additionalPay;
    
    return {
      regular_pay: Math.round(regularPay * 100) / 100,
      holiday_pay: Math.round(holidayPay * 100) / 100,
      additional_pay: Math.round(additionalPay * 100) / 100,
      total_pay: Math.round(totalPay * 100) / 100
    };
  },

  // Record holiday work for an employee
  async recordHolidayWork(
    employeeId: string,
    holidayId: string,
    date: string,
    hoursWorked: number,
    hourlyRate: number,
    notes?: string
  ): Promise<HolidayWorkRecord | null> {
    try {
      // First, get the holiday details
      const { data: holiday, error: holidayError } = await supabase
        .from('philippine_holidays')
        .select('*')
        .eq('id', holidayId)
        .single();

      if (holidayError || !holiday) {
        console.error('Holiday not found:', holidayError);
        return null;
      }

      // Calculate holiday pay
      const payCalculation = this.calculateHolidayPay(
        hourlyRate,
        hoursWorked,
        holiday.type
      );

      // Check if record already exists
      const { data: existingRecord } = await supabase
        .from('holiday_work_records')
        .select('id')
        .eq('employee_id', employeeId)
        .eq('holiday_id', holidayId)
        .eq('date', date)
        .single();

      if (existingRecord) {
        // Update existing record
        const { data, error } = await supabase
          .from('holiday_work_records')
          .update({
            hours_worked: hoursWorked,
            regular_pay: payCalculation.regular_pay,
            holiday_pay: payCalculation.holiday_pay,
            additional_pay: payCalculation.additional_pay,
            total_pay: payCalculation.total_pay,
            notes: notes,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingRecord.id)
          .select('*')
          .single();

        if (error) {
          console.error('Error updating holiday work record:', error);
          return null;
        }

        return data;
      } else {
        // Create new record
        const { data, error } = await supabase
          .from('holiday_work_records')
          .insert({
            employee_id: employeeId,
            holiday_id: holidayId,
            date: date,
            hours_worked: hoursWorked,
            regular_pay: payCalculation.regular_pay,
            holiday_pay: payCalculation.holiday_pay,
            additional_pay: payCalculation.additional_pay,
            total_pay: payCalculation.total_pay,
            notes: notes
          })
          .select('*')
          .single();

        if (error) {
          console.error('Error creating holiday work record:', error);
          return null;
        }

        return data;
      }
    } catch (error) {
      console.error('Error in recordHolidayWork:', error);
      return null;
    }
  },

  // Get holiday work records for an employee within a date range
  async getEmployeeHolidayWork(
    employeeId: string,
    startDate: string,
    endDate: string
  ): Promise<HolidayWorkRecord[]> {
    try {
      const { data, error } = await supabase
        .from('holiday_work_records')
        .select(`
          *,
          holiday:philippine_holidays(*)
        `)
        .eq('employee_id', employeeId)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching employee holiday work:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getEmployeeHolidayWork:', error);
      return [];
    }
  },

  // Get all holiday work records within a date range
  async getAllHolidayWork(
    startDate: string,
    endDate: string
  ): Promise<HolidayWorkRecord[]> {
    try {
      const { data, error } = await supabase
        .from('holiday_work_records')
        .select(`
          *,
          holiday:philippine_holidays(*),
          employee:employees(
            first_name,
            last_name,
            position,
            department
          )
        `)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching all holiday work:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllHolidayWork:', error);
      return [];
    }
  },

  // Generate holiday pay summary for an employee
  async getEmployeeHolidayPaySummary(
    employeeId: string,
    startDate: string,
    endDate: string
  ): Promise<HolidayPaySummary | null> {
    try {
      const holidayRecords = await this.getEmployeeHolidayWork(
        employeeId,
        startDate,
        endDate
      );

      if (holidayRecords.length === 0) {
        return null;
      }

      // Get employee details
      const { data: employee, error: employeeError } = await supabase
        .from('employees')
        .select('first_name, last_name')
        .eq('id', employeeId)
        .single();

      if (employeeError || !employee) {
        console.error('Employee not found:', employeeError);
        return null;
      }

      const totalHolidayHours = holidayRecords.reduce(
        (sum, record) => sum + record.hours_worked,
        0
      );
      const totalHolidayPay = holidayRecords.reduce(
        (sum, record) => sum + record.holiday_pay,
        0
      );
      const totalAdditionalPay = holidayRecords.reduce(
        (sum, record) => sum + record.additional_pay,
        0
      );
      const totalEarnings = totalHolidayPay + totalAdditionalPay;

      const holidaysWorked = holidayRecords.map(record => ({
        holiday_name: record.holiday?.name || 'Unknown Holiday',
        date: record.date,
        type: record.holiday?.type || 'unknown',
        hours_worked: record.hours_worked,
        pay_earned: record.total_pay
      }));

      return {
        employee_id: employeeId,
        employee_name: `${employee.first_name} ${employee.last_name}`,
        total_holiday_hours: totalHolidayHours,
        total_holiday_pay: totalHolidayPay,
        total_additional_pay: totalAdditionalPay,
        total_earnings: totalEarnings,
        holidays_worked: holidaysWorked
      };
    } catch (error) {
      console.error('Error in getEmployeeHolidayPaySummary:', error);
      return null;
    }
  },

  // Process holiday work from attendance records
  async processHolidayWorkFromAttendance(
    employeeId: string,
    date: string,
    hoursWorked: number,
    employeeSalary: number
  ): Promise<HolidayWorkRecord | null> {
    try {
      // Check if the date is a holiday based on month and day only
      const holidayCheck = await this.isHolidayByMonthDay(date);
      
      if (!holidayCheck.is_holiday || !holidayCheck.holiday_id) {
        return null; // Not a holiday, no processing needed
      }

      // Calculate hourly rate
      const hourlyRate = calculateHourlyRate(employeeSalary);

      // Record holiday work
      return await this.recordHolidayWork(
        employeeId,
        holidayCheck.holiday_id,
        date,
        hoursWorked,
        hourlyRate,
        `Auto-generated from attendance on ${holidayCheck.holiday_name} (detected by month/day pattern)`
      );
    } catch (error) {
      console.error('Error in processHolidayWorkFromAttendance:', error);
      return null;
    }
  },

  // Delete holiday work record
  async deleteHolidayWorkRecord(recordId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('holiday_work_records')
        .delete()
        .eq('id', recordId);

      if (error) {
        console.error('Error deleting holiday work record:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteHolidayWorkRecord:', error);
      return false;
    }
  },

  // Add new holiday (admin function)
  async addHoliday(holiday: Omit<PhilippineHoliday, 'id' | 'created_at' | 'updated_at'>): Promise<PhilippineHoliday | null> {
    try {
      const { data, error } = await supabase
        .from('philippine_holidays')
        .insert(holiday)
        .select('*')
        .single();

      if (error) {
        console.error('Error adding holiday:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in addHoliday:', error);
      return null;
    }
  },

  // Update holiday (admin function)
  async updateHoliday(holidayId: string, updates: Partial<PhilippineHoliday>): Promise<PhilippineHoliday | null> {
    try {
      const { data, error } = await supabase
        .from('philippine_holidays')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', holidayId)
        .select('*')
        .single();

      if (error) {
        console.error('Error updating holiday:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in updateHoliday:', error);
      return null;
    }
  },

  // Delete holiday (admin function)
  async deleteHoliday(holidayId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('philippine_holidays')
        .delete()
        .eq('id', holidayId);

      if (error) {
        console.error('Error deleting holiday:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteHoliday:', error);
      return false;
    }
  }
};